import { useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { api, DYNAMICS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DiscoverProfileCard } from "../components/DiscoverProfileCard";

const DiscoverFilters = ({ filter, onSelect, isPremium }) => (
  <div className="mt-10 flex flex-wrap gap-3">
    <button
      data-testid="filter-all"
      onClick={() => onSelect("")}
      className={`px-5 py-2.5 border text-sm transition-colors duration-300 ${
        !filter
          ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
          : "border-[#E5E0D8] text-[#5C5C5C] hover:border-[#1A1A1A]"
      }`}
    >
      All dynamics
    </button>
    {DYNAMICS.map((d) => (
      <button
        key={d.key}
        data-testid={`filter-${d.key}`}
        onClick={() => onSelect(d.key)}
        className={`flex items-center gap-2 px-5 py-2.5 border text-sm transition-colors duration-300 ${
          filter === d.key
            ? "bg-[#7A3B3F] text-[#FAF9F6] border-[#7A3B3F]"
            : "border-[#E5E0D8] text-[#5C5C5C] hover:border-[#7A3B3F]"
        }`}
      >
        {!isPremium && <Lock className="w-3 h-3" strokeWidth={1.5} />}
        {d.label}
      </button>
    ))}
  </div>
);

export default function Discover() {
  const [profiles, setProfiles] = useState(null);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(async (dyn) => {
    setProfiles(null);
    const { data } = await api.get("/discover", { params: dyn ? { dynamic: dyn } : {} });
    setProfiles(data);
  }, [setProfiles]);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const selectFilter = (key) => {
    if (key && !user?.is_premium) {
      toast("Dynamic filters are a Premium feature", {
        action: { label: "Upgrade", onClick: () => navigate("/premium") },
      });
      return;
    }
    setFilter(key);
  };

  const swipe = async (target, action) => {
    const prev = profiles;
    setProfiles((p) => p.filter((x) => x.id !== target.id));
    try {
      const { data } = await api.post("/swipe", { target_id: target.id, action });
      if (data.matched) {
        toast.success(`It's a match with ${data.match_name}!`, {
          action: { label: "Say hello", onClick: () => navigate(`/chat/${data.match_id}`) },
          duration: 8000,
        });
      }
    } catch (err) {
      setProfiles(prev);
      if (err.response?.status === 403 && err.response?.data?.detail === "daily_like_limit") {
        toast.error("You've used your 5 free likes today.", {
          description: "Go Premium for unlimited likes.",
          action: { label: "Upgrade", onClick: () => navigate("/premium") },
          duration: 8000,
        });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16" data-testid="discover-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A3B3F]">Discover</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">
        People building toward marriage.
      </h1>

      <DiscoverFilters filter={filter} onSelect={selectFilter} isPremium={user?.is_premium} />

      <div className="mt-16 space-y-16">
        {profiles === null && (
          <p className="text-[#8A8A8A]" data-testid="discover-loading">
            Finding people of intention…
          </p>
        )}
        {profiles?.length === 0 && (
          <div className="py-24 text-center" data-testid="discover-empty">
            <p className="font-serif text-2xl text-[#5C5C5C]">You've met everyone here, for now.</p>
            <p className="mt-3 text-[#8A8A8A]">Check back soon, or adjust your filter.</p>
          </div>
        )}
        <AnimatePresence>
          {profiles?.map((p, i) => (
            <DiscoverProfileCard key={p.id} profile={p} index={i} onSwipe={swipe} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
